import { useAuth } from 'hooks';
import { getUserSession } from 'lib/firebase';
import { useEffect, useMemo, useState } from 'react';
import {
    createDefaultSession,
    createDefaultIssuesUserSession,
    createDefaultEfficiencyAccountsUserSession,
    createDefaultUserPreferencesSession,
    createDefaultDeviceTicketsSession,
    createDefaultDeviceDeliveriesSession,
    createDefaultMapSession,
    createDefaultOrganizationGroupSession,
    createDefaultDeliveryTypesSession,
    createDefaultDeviceUsersSession,
    createDefaultOfflineMonitorSession,
    createDefaultShippedMonitorSession,
    createDefaultSleepingMonitorSession,
    createDefaultLowBatteryMonitorSession,
    createDefaultConsumersUserSession,
    createDefaultConsumerDevicesSession,
} from 'store/services';
import { UserSession } from 'types/User/firebaseTypes';

/** Hook to get the logged in user's session data including column settings and filters. */
export const useUserSession = () => {
    const [userSession, setUserSession] = useState<UserSession>();

    const { user } = useAuth();

    useEffect(() => {
        if (user?.uid) {
            getUserSession(user?.uid).then((session) => {
                if (session) {
                    // Set defaults for missing session entries (i.e., efficiencyAccounts, userPreferences, etc. ).
                    // NOTE: if we add new fields to entries and need to set defaults for those, we will have to do so on a case by case basis, making sure that we don't wipe out the other existing settings in that entry.
                    // These changes are NOT saved to the user session in firebase until the user interacts with a particular page.
                    // E.g., Any new defaults for the Issues page are not saved to the issues session entry until the user goes to the Issues page
                    // and changes something like column order.
                    setUserSession({
                        ...session,
                        ...{
                            issues:
                                session.issues ??
                                createDefaultIssuesUserSession(),
                        },
                        ...{
                            efficiencyAccounts:
                                session.efficiencyAccounts ??
                                createDefaultEfficiencyAccountsUserSession(),
                        },
                        ...{
                            userPreferences:
                                session.userPreferences ??
                                createDefaultUserPreferencesSession(),
                        },
                        ...{
                            DeviceTickets:
                                session.DeviceTickets ??
                                createDefaultDeviceTicketsSession(),
                        },
                        ...{
                            DeviceDeliveryTickets:
                                session.DeviceDeliveryTickets ??
                                createDefaultDeviceDeliveriesSession(),
                        },
                        ...{ map: session.map ?? createDefaultMapSession() },
                        ...{
                            organizationGroups:
                                session.organizationGroups ??
                                createDefaultOrganizationGroupSession(),
                        },
                        ...{
                            organizationDeliveryTypes:
                                session.organizationDeliveryTypes ??
                                createDefaultDeliveryTypesSession(),
                        },
                        ...{
                            deviceUsers:
                                session.deviceUsers ??
                                createDefaultDeviceUsersSession(),
                        },
                        ...{
                            offlineMonitors:
                                session.offlineMonitors ??
                                createDefaultOfflineMonitorSession(),
                        },
                        ...{
                            shippedMonitors:
                                session.shippedMonitors ??
                                createDefaultShippedMonitorSession(),
                        },
                        ...{
                            sleepingMonitors:
                                session.sleepingMonitors ??
                                createDefaultSleepingMonitorSession(),
                        },
                        ...{
                            lowBatteryMonitors:
                                session.lowBatteryMonitors ??
                                createDefaultLowBatteryMonitorSession(),
                        },
                        ...{
                            consumers:
                                session.consumers ??
                                createDefaultConsumersUserSession(),
                        },
                        ...{
                            consumerDevices:
                                session.consumerDevices ??
                                createDefaultConsumerDevicesSession(),
                        },
                    });
                }
                // Creating an entire user session from scratch.
                else setUserSession(createDefaultSession());
            });
        }
    }, [user]);

    return useMemo(() => userSession, [userSession]);
};
